import React, { useRef, useEffect, useState } from 'react';
import { ArrowLeft, Eye, Pencil } from 'lucide-react';
import StepProgress from './StepProgress';
import WebsitePreviewPanel from './WebsitePreviewPanel';
import { Button } from '@/components/ui/button';
import artistIntroLogo from '../../assets/artist_intro.svg';

interface OnboardingLayoutProps {
  currentStep: number;
  totalSteps: number;
  title: string;
  description: string;
  showLivePreview?: boolean;
  previewSection?: string;
  previewPatch?: Record<string, unknown>;
  isValid: boolean;
  isSaving: boolean;
  onContinue: () => void;
  onSkip: () => void;
  onBack?: () => void;
  showSkip?: boolean;
  children: React.ReactNode;
}

const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({
  currentStep,
  totalSteps,
  title,
  description,
  isValid,
  isSaving,
  onContinue,
  onSkip,
  onBack,
  showSkip = true,
  showLivePreview = false,
  previewSection,
  previewPatch,
  children,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [mobileView, setMobileView] = useState<'edit' | 'preview'>('edit');

  // Reset scroll to top when the step changes
  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
    setMobileView('edit');
  }, [currentStep]);

  return (
    <div className="h-full flex flex-col bg-white rounded-2xl overflow-hidden">
      {/* Mobile-only header */}
      <div className="lg:hidden px-4 pt-4 sm:px-6 sm:pt-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <img src={artistIntroLogo} alt="Artist Intro" className="h-8" />
          {showLivePreview && (
            <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1">
              <button
                type="button"
                onClick={() => setMobileView('edit')}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${mobileView === 'edit' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
              >
                <Pencil className="size-3.5" />
                Edit
              </button>
              <button
                type="button"
                onClick={() => setMobileView('preview')}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${mobileView === 'preview' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
              >
                <Eye className="size-3.5" />
                Preview
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900 shrink-0 cursor-pointer"
              aria-label="Go back"
              title="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="flex-1">
            <StepProgress currentStep={currentStep} totalSteps={totalSteps} />
          </div>
        </div>
      </div>

      <div className={`flex flex-1 min-h-0 flex-col overflow-hidden ${showLivePreview ? 'lg:grid lg:grid-cols-5' : ''}`}>
        {/* <div className="flex flex-col lg:grid lg:grid-cols-5 flex-1 min-h-0 overflow-hidden"></div> */}
        {/* ─── Left Column: Primary Content ───────────────── */}
        <div className={`${showLivePreview && mobileView === 'preview' ? 'hidden lg:flex' : 'flex'} ${showLivePreview ? 'lg:col-span-2' : 'w-full'} flex-col flex-1 min-h-0 overflow-hidden`}>
          {/* Fixed top: logo + step progress (desktop only — mobile header is above) */}
          <div className="shrink-0 hidden lg:block px-6 sm:px-10 lg:px-16 pt-8 lg:pt-10 pb-4 bg-white border-b border-gray-100">
            <div className="max-w-2xl mx-auto w-full">
              {/* Desktop header: logo + progress */}
              <div>
                <img src={artistIntroLogo} alt="Artist Intro" className="h-10 mb-8" />
                <div className="flex items-center gap-3">
                  {onBack && (
                    <button
                      type="button"
                      onClick={onBack}
                      className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900 shrink-0 cursor-pointer"
                      aria-label="Go back"
                      title="Back"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                  )}
                  <div className="flex-1">
                    <StepProgress currentStep={currentStep} totalSteps={totalSteps}/>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable middle: step title + content */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain px-6 sm:px-10 lg:px-16">
            <div className="max-w-2xl mx-auto w-full py-6">
              {/* Step header */}
              <div className="mb-5 sm:mb-8">
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
                  {title}
                </h1>
                <p className="text-gray-500 text-sm sm:text-base">
                  {description}
                </p>
              </div>

              {/* Step component */}
              <div className="pb-4">
                {children}
              </div>
            </div>
          </div>

          {/* Fixed bottom: action bar */}
          <div className="shrink-0 bg-white border-t border-gray-100 px-6 sm:px-10 lg:px-16 py-4">
            <div className="max-w-2xl mx-auto w-full flex items-center justify-between">
              {showSkip ? (
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={onSkip}
                  disabled={isSaving}
                >
                  Skip for now
                </Button>
              ) : (
                <div />
              )}
              <Button
                variant="primary"
                type="button"
                onClick={onContinue}
                disabled={!isValid || isSaving}
                loading={isSaving}
                loadingText="Saving…"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>

        {showLivePreview && (
          <div className={`${mobileView === 'preview' ? 'flex' : 'hidden'} lg:flex lg:col-span-3 min-h-0 flex-col border-t border-border bg-muted/30 lg:border-l lg:border-t-0`}>
            <WebsitePreviewPanel section={previewSection} patch={previewPatch} statusText="Updates as you edit" />
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingLayout;
